import Provider from '@/enums/Provider';
import UrlTypes from '@/enums/UrlTypes';
import IPrice from '@/interfaces/IPrice';
import ISpecification from '@/interfaces/ISpecification';
import IDescription from '@/interfaces/IDescription';
import IImage from '@/interfaces/IImage';
import { extractMetaData } from '@/helpers/extractMetaData';
import { extractProductName } from '@/helpers/product/extractProductName';
import { extractProductPrice } from '@/helpers/product/extractProductPrice';
import { extractProductSpecification } from '@/helpers/product/extractProductSpecification';
import { extractProductDescription } from '@/helpers/product/description/extractProductDescription';
import { extractProductImages } from '@/helpers/product/extractProductImages';
import { extractImagesFromHtml } from '@/helpers/images/extractImagesFromHtml';
import { processHtml } from '@/helpers/html/htmlProcessor';
import { processImages } from '@/helpers/images/imageProcessor';

/**
 * Extracts product data from the provided HTML source.
 *
 * @param {string} htmlSource - The HTML content of the product page.
 * @param {string} pathname - The URL pathname of the product page.
 * @param {Provider} provider - The provider enum indicating the source of the product data.
 * @param {string} baseUrl - The base URL used for processing images and links.
 * @returns {{
 *     name: string,
 *     metaTitle: string,
 *     metaDescription: string,
 *     price: IPrice,
 *     specification: ISpecification,
 *     description: IDescription,
 *     images: IImage[]
 * }} An object containing the extracted product data, including name, metadata, price, specifications, description, and images.
 */
export function extractProductData(
    htmlSource: string,
    pathname: string,
    provider: Provider,
    baseUrl: string
): {
    name: string;
    metaTitle: string;
    metaDescription: string;
    price: IPrice;
    specification: ISpecification;
    description: IDescription;
    images: IImage[];
} {
    // metadata
    const { metaTitle, metaDescription } = extractMetaData(htmlSource, provider);

    // name, price, spec
    const name = extractProductName(htmlSource, pathname, provider);
    const price = extractProductPrice(htmlSource, provider);
    const specification = extractProductSpecification(htmlSource, provider);

    // description + its images
    const rawDescription = extractProductDescription(htmlSource, pathname, provider);
    const fallbackDescriptionImages = extractImagesFromHtml(
        rawDescription.descriptionHtml,
        pathname,
        UrlTypes.PRODUCT_DESCRIPTION
    );
    const descriptionImagesRaw = [...rawDescription.images, ...fallbackDescriptionImages];

    // clean HTML & normalize description images
    const descriptionHtml = processHtml(rawDescription.descriptionHtml, descriptionImagesRaw, baseUrl);
    const description: IDescription = {
        pathname,
        descriptionHtml,
        images: processImages(descriptionImagesRaw, baseUrl),
    };

    // main product images
    const rawMainImages = extractProductImages(htmlSource, pathname, provider);
    const images = processImages(rawMainImages, baseUrl);

    return {
        name,
        metaTitle,
        metaDescription,
        price,
        specification,
        description,
        images,
    };
}
